import { useEffect, useRef, useState } from "react";
import { CheckCircle2, CircleAlert, LoaderCircle, Radio, Wifi } from "lucide-react";
import { api, type AgentRun, type AgentRunEvent } from "./api";

type Props = { projectId: string; goal: string; enabled?: boolean; disabledReason?: string; onStarted?: () => void };

type Transport = "idle" | "connecting" | "websocket+sse-replay" | "sse-fallback" | "closed";

function appendEvent(current: AgentRunEvent[], event: AgentRunEvent) {
  if (event.sequence !== undefined && current.some(item => item.sequence === event.sequence)) return current;
  return [...current, event].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
}

export function AgentRunPanel({ projectId, goal, enabled = true, disabledReason, onStarted }: Props) {
  const [run, setRun] = useState<AgentRun | null>(null);
  const [events, setEvents] = useState<AgentRunEvent[]>([]);
  const [transport, setTransport] = useState<Transport>("idle");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [lastGoal, setLastGoal] = useState("");
  const sessionRef = useRef<string | undefined>(undefined);

  async function start() {
    const requestGoal = goal.trim() || lastGoal;
    if (!enabled || !requestGoal || pending) return;
    setPending(true); setError(""); setEvents([]);
    try {
      setLastGoal(requestGoal);
      if (!sessionRef.current) sessionRef.current = (await api.agent.createSession(projectId)).session.id;
      const result = await api.agent.sendMessage(sessionRef.current, requestGoal, navigator.language);
      setRun({ ...result.run, resolved_locale: result.locale }); onStarted?.();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Agent run could not start.");
    } finally { setPending(false); }
  }

  useEffect(() => {
    if (!run?.id) return;
    setTransport("connecting");
    const eventSource = new EventSource(`/api/v1/agent/runs/${encodeURIComponent(run.id)}/events?after=0`);
    const socketUrl = `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}/api/v1/agent/runs/${encodeURIComponent(run.id)}/websocket`;
    const socket = new WebSocket(socketUrl);
    const onAgentEvent = (message: MessageEvent) => {
      try { setEvents(current => appendEvent(current, JSON.parse(message.data) as AgentRunEvent)); } catch { /* ignore malformed telemetry */ }
    };
    const onComplete = (message: MessageEvent) => {
      try { setRun(current => current ? { ...current, state: JSON.parse(message.data).state || current.state } : current); } catch { /* keep persisted state */ }
    };
    eventSource.addEventListener("agent.run", onAgentEvent);
    eventSource.addEventListener("complete", onComplete);
    eventSource.onerror = () => { if (socket.readyState !== WebSocket.OPEN) setTransport("sse-fallback"); };
    socket.onopen = () => setTransport("websocket+sse-replay");
    socket.onmessage = message => {
      try {
        const payload = JSON.parse(message.data) as { type?: string; id?: number; data?: AgentRunEvent };
        if (payload.type === "heartbeat") return;
        if (payload.data) setEvents(current => appendEvent(current, payload.data!));
      } catch { /* ignore malformed websocket payload */ }
    };
    socket.onerror = () => { if (eventSource.readyState === EventSource.OPEN || eventSource.readyState === EventSource.CONNECTING) setTransport("sse-fallback"); };
    socket.onclose = () => setTransport(current => current === "websocket+sse-replay" ? "closed" : current);
    return () => { eventSource.close(); socket.close(1000, "workbench_cleanup"); };
  }, [run?.id]);

  const completed = run?.state === "completed" || run?.state === "partially_completed";
  return <section className="agent-run-card" aria-live="polite">
    <div className="agent-run-head"><div><strong>Agent OS execution</strong><span>{run ? `Run ${run.state}` : "Production gateway ready"}</span></div><span className={`agent-transport ${transport}`}><Radio size={13} />{transport === "websocket+sse-replay" ? "Live + replay" : transport === "sse-fallback" ? "SSE fallback" : transport === "connecting" ? "Connecting…" : transport === "closed" ? "Closed" : "Idle"}</span></div>
    {!run && <p className="agent-run-copy">{disabledReason || "Run this goal through the authenticated Agent Gateway with project-scoped audit and verified side effects."}</p>}
    {run && <div className="agent-run-meta"><span><Wifi size={13} />{events.length} events</span><span>Locale: {run.resolved_locale || "auto"}</span><span>Correlation: {run.correlation_id?.slice(0, 8) || "—"}</span></div>}
    {run && <div className="agent-run-status">{completed ? <CheckCircle2 size={15} /> : <LoaderCircle className="activity-spinner" size={15} />}<span>{completed ? "Completed and verified" : `Agent state: ${run.state}`}</span></div>}
    {events.length > 0 && <ul className="agent-run-events">{events.slice(-5).map(event => <li key={`${event.sequence}-${event.id || event.type}`}><span>{event.type || "agent.run"}</span><small>{String(event.to_state || event.payload?.message || "event received")}</small></li>)}</ul>}
    {error && <p className="agent-run-error" role="alert"><CircleAlert size={14} />{error}</p>}
    <button className="button secondary agent-run-button" type="button" onClick={start} disabled={!enabled || ((!goal.trim() && !lastGoal) || pending)}>{pending ? "Starting Agent…" : run ? "Run again" : "Run with Agent OS"}</button>
  </section>;
}
